"use client";

import { useState, useCallback, useEffect } from "react";
import { Menu } from "lucide-react";
import { Sidebar } from "./sidebar";
import { Toaster } from "@/components/ui/sonner";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useAuth } from "@/hooks/use-auth";
import { useVersionBeacon } from "@/hooks/use-version-beacon";

interface AppLayoutProps {
  children: React.ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
  const { user, loading } = useAuth();
  // #237: poll de versão no visibilitychange — detecta aba rodando build
  // antigo mesmo com o socket morto (a fonte primária é o server:version).
  useVersionBeacon();
  const [isDark, setIsDark] = useState(() => {
    if (typeof window === "undefined") return true;
    const saved = localStorage.getItem("theme");
    return saved ? saved === "dark" : true;
  });

  useEffect(() => {
    document.documentElement.classList.toggle("dark", isDark);
    localStorage.setItem("theme", isDark ? "dark" : "light");
  }, [isDark]);

  const toggleTheme = useCallback(() => {
    setIsDark((prev) => !prev);
  }, []);

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="text-muted-foreground">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden bg-sidebar">
      {/* Sidebar - desktop */}
      <div className="hidden md:block gk-vt-sidebar">
        <Sidebar onToggleTheme={toggleTheme} isDark={isDark} user={user} />
      </div>

      {/* Main content — view transition cuida da entrada via next-view-transitions */}
      <div className="flex flex-1 flex-col overflow-hidden rounded-l-2xl border border-border bg-background gk-vt-shell">
        {/* Mobile top bar */}
        <div className="flex h-12 items-center px-4 border-b border-border bg-background md:hidden">
          <Sheet>
            <SheetTrigger
              render={
                <button className="inline-flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground hover:bg-accent/10">
                  <Menu className="h-5 w-5" />
                </button>
              }
            />
            <SheetContent side="left" className="w-64 p-0">
              <Sidebar onToggleTheme={toggleTheme} isDark={isDark} user={user} />
            </SheetContent>
          </Sheet>
          <div className="flex items-center ml-2">
            <img src="/logo-gk.svg" alt="GK Platform" className="h-8 w-auto object-contain" />
          </div>
        </div>

        <main className="flex-1 overflow-y-auto p-6 bg-[oklch(0.9700_0.0055_274.79)] dark:bg-background">{children}</main>
      </div>

      <Toaster richColors position="top-right" />
    </div>
  );
}
